import { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import SupportThread from './SupportThread';
import VocationWorksLogo from './VocationWorksLogo';
import LineIcon from './LineIcon';

const NAVY      = '#0D1B2A';
const NAVY_MID  = '#132233';
const AVATAR_BG = NAVY_MID;
const AMBER     = '#F5A623';
const WHITE     = '#FFFFFF';
const FONT_BODY = "'DM Sans', sans-serif";

// The account's "Vocation Works Support" conversation, shared by every dashboard (worker,
// employer, union, organization). The dashboard owns it, so the same chat opens from the
// Messages list (a normal conversation row once it has a message, plus the "Message support"
// link at the end of the list) and from the dashboard's Help entries on any tab.
// Returns { conversation, lastMessage, hasUnread, contacting, open, modal }: render `modal`
// once in the dashboard; open() shows the existing conversation, or finds/creates the open
// one when there is none yet.
export function useSupportInbox({ supabase, accountType, accountId, viewerId }) {
  const { t } = useTranslation();
  const [conversation, setConversation] = useState(null);
  const [lastMessage, setLastMessage]   = useState(null);
  const [unreadCount, setUnreadCount]   = useState(0);
  const [showModal, setShowModal]       = useState(false);
  const [contacting, setContacting]     = useState(false);
  const [isMobile, setIsMobile]         = useState(window.innerWidth < 640);
  const contactingRef = useRef(false);

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < 640);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const refreshSummary = async (convId) => {
    const { data: msgs } = await supabase
      .from('support_messages')
      .select('*')
      .eq('conversation_id', convId)
      .order('created_at', { ascending: false })
      .limit(1);
    setLastMessage(msgs?.[0] || null);

    const { count } = await supabase
      .from('support_messages')
      .select('id', { count: 'exact', head: true })
      .eq('conversation_id', convId)
      .eq('sender_type', 'admin')
      .is('read_at', null);
    setUnreadCount(count || 0);
  };

  const loadConversation = async () => {
    if (!accountId) return;
    const { data } = await supabase
      .from('support_conversations')
      .select('*')
      .eq('account_type', accountType)
      .eq('account_id', accountId)
      .order('updated_at', { ascending: false })
      .limit(1)
      .maybeSingle();
    setConversation(data || null);
    if (data) refreshSummary(data.id);
  };

  useEffect(() => {
    loadConversation();
  }, [supabase, accountType, accountId]); // eslint-disable-line

  // New conversations can arrive at any time -- RLS scopes this subscription to rows
  // this account can already see, so no explicit filter is needed here.
  useEffect(() => {
    if (!accountId) return;
    const channel = supabase
      .channel(`support-inbox-conv:${accountType}:${accountId}`)
      .on('postgres_changes', { event: '*', schema: 'public', table: 'support_conversations' }, () => loadConversation())
      .subscribe();
    return () => supabase.removeChannel(channel);
  }, [supabase, accountType, accountId]); // eslint-disable-line

  useEffect(() => {
    if (!conversation?.id) return;
    const channel = supabase
      .channel(`support-inbox-msgs:${conversation.id}`)
      .on('postgres_changes', { event: 'INSERT', schema: 'public', table: 'support_messages', filter: `conversation_id=eq.${conversation.id}` }, () => {
        if (!showModal) refreshSummary(conversation.id);
      })
      .subscribe();
    return () => supabase.removeChannel(channel);
  }, [supabase, conversation?.id, showModal]); // eslint-disable-line

  // Closing the modal means SupportThread just marked everything read -- resync the badge.
  const handleCloseModal = () => {
    setShowModal(false);
    if (conversation?.id) refreshSummary(conversation.id);
  };

  // Mirrors the admin picker's find-or-create logic (AdminSupportMessages.jsx) so an
  // account never ends up with duplicate open conversations with support.
  const handleContactSupport = async () => {
    if (contactingRef.current) return;
    contactingRef.current = true;
    setContacting(true);

    const { data: existing } = await supabase
      .from('support_conversations')
      .select('*')
      .eq('account_type', accountType)
      .eq('account_id', accountId)
      .eq('status', 'open')
      .maybeSingle();

    let conv = existing;
    if (!conv) {
      const { data: created, error } = await supabase
        .from('support_conversations')
        .insert({ account_type: accountType, account_id: accountId })
        .select('*')
        .single();
      if (error) {
        if (error.code === '23505') {
          // Lost a true-parallel race to the DB's one-open-conversation-per-account
          // constraint -- the other insert won, so just fetch and use that one.
          const { data: winner } = await supabase
            .from('support_conversations')
            .select('*')
            .eq('account_type', accountType)
            .eq('account_id', accountId)
            .eq('status', 'open')
            .maybeSingle();
          conv = winner;
        }
        if (!conv) {
          alert(`${t('support.couldNotStartConversationPrefix')}${error.message}`);
          contactingRef.current = false;
          setContacting(false);
          return;
        }
      } else {
        conv = created;
      }
    }

    setConversation(conv);
    refreshSummary(conv.id);
    setShowModal(true);
    contactingRef.current = false;
    setContacting(false);
  };

  // Same paths the old pinned entry used: open the existing conversation, or find-or-create one.
  const open = () => {
    if (!accountId || contactingRef.current) return;
    if (conversation) setShowModal(true);
    else handleContactSupport();
  };

  const modal = showModal && conversation ? (
    <div
      onClick={e => { if (e.target === e.currentTarget) handleCloseModal(); }}
      style={{
        // Phone: the thread fills the screen above the dashboard's bottom nav (58px + safe
        // area, the same in every dashboard that hosts this), so the composer sits above it.
        position: 'fixed', inset: 0, zIndex: 1000,
        bottom: isMobile ? 'calc(58px + env(safe-area-inset-bottom))' : 0,
        backgroundColor: 'rgba(0,0,0,0.65)',
        display: 'flex', alignItems: isMobile ? 'stretch' : 'center', justifyContent: 'center',
        padding: isMobile ? 0 : '16px',
      }}
    >
      <div style={{
        backgroundColor: NAVY, width: '100%',
        maxWidth: isMobile ? '100%' : '460px',
        height: isMobile ? '100%' : '640px',
        maxHeight: isMobile ? '100%' : '85vh',
        borderRadius: isMobile ? 0 : '14px',
        border: isMobile ? 'none' : '1px solid rgba(255,255,255,0.12)',
        display: 'flex', flexDirection: 'column', overflow: 'hidden',
      }}>
        <SupportThread
          supabase={supabase}
          conversationId={conversation.id}
          viewerType="account"
          viewerId={viewerId}
          title={t('support.vocationWorksSupport')}
          subtitle={conversation.status === 'closed' ? t('support.statusClosed') : t('support.repliesWithinDayNote')}
          avatarLabel={<VocationWorksLogo variant="reversed" layout="mark" size={18} />}
          avatarColor={AVATAR_BG}
          onBack={handleCloseModal}
        />
      </div>
    </div>
  ) : null;

  return { conversation, lastMessage, hasUnread: unreadCount > 0, contacting, open, modal };
}

// "Help" entry above Sign out in a dashboard's desktop sidebar. Opens the support chat.
export function HelpSidebarButton({ onClick }) {
  const { t } = useTranslation();
  return (
    <button
      type="button"
      data-help-entry="sidebar"
      onClick={onClick}
      style={{
        display: 'flex', alignItems: 'center', gap: '8px', minHeight: '44px',
        background: 'none', border: 'none', cursor: 'pointer',
        padding: '8px 10px', borderRadius: '8px', width: '100%', marginBottom: '4px',
        color: 'rgba(255,255,255,0.75)', fontFamily: FONT_BODY, fontSize: '14px', fontWeight: 600,
      }}
    >
      <LineIcon name="help" size={16} color="currentColor" strokeWidth={2} />
      {t('support.help')}
    </button>
  );
}

// "Help" card on a dashboard's Settings tab (the entry point on phones, which have no sidebar).
export function HelpSettingsCard({ onClick, style }) {
  const { t } = useTranslation();
  return (
    <div data-help-entry="settings" style={{ background: NAVY_MID, borderRadius: '12px', padding: '20px', ...style }}>
      <h3 style={{ display: 'flex', alignItems: 'center', gap: '8px', fontFamily: FONT_BODY, fontSize: '0.95rem', fontWeight: 700, color: WHITE, marginBottom: '6px' }}>
        <LineIcon name="help" size={18} color={AMBER} strokeWidth={2} />
        {t('support.help')}
      </h3>
      <p style={{ fontSize: '14px', color: 'rgba(255,255,255,0.55)', lineHeight: 1.5, marginBottom: '14px' }}>{t('support.helpNote')}</p>
      <button
        type="button"
        onClick={onClick}
        style={{
          minHeight: '44px', background: 'transparent', border: `1px solid ${AMBER}`,
          color: AMBER, fontFamily: FONT_BODY, fontSize: '14px', fontWeight: 700,
          padding: '0 22px', borderRadius: '8px', cursor: 'pointer',
        }}
      >
        {t('support.messageSupport')}
      </button>
    </div>
  );
}
